import React, { useEffect, useRef, useState } from 'react';
import { MessagesSquare, Palette, Rocket, ArrowRight } from 'lucide-react';

const WorkingProcess = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: <MessagesSquare className="w-8 h-8" />,
      number: "01",
      title: "Consultation",
      description: "We begin with a thoughtful conversation about your vision, your books, and your readers. Understanding your unique voice is the foundation of everything we create.",
      accent: "Your story, understood"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      number: "02",
      title: "Design & Craft",
      description: "Our designers translate your brand into a bespoke digital experience. Every element is carefully considered to reflect your literary identity and engage your audience.",
      accent: "Tailored to perfection"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      number: "03",
      title: "Launch & Flourish",
      description: "We launch your site and provide ongoing guidance to ensure it thrives. Your platform evolves as your author career grows, always ready to captivate new readers.",
      accent: "Your journey continues"
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-cream-100 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%231a1a2e' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
              How It Works
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
            A Refined Process
            <span className="block mt-2 text-gradient-gold">From Vision to Reality</span>
          </h2>

          <p className="font-body text-lg text-ink-600 leading-relaxed">
            Every author's journey is unique. Our process adapts to your needs while ensuring exceptional results.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-0 relative">
          {/* Connector Line (Desktop) */}
          <div className="hidden lg:block absolute top-24 left-[20%] right-[20%] h-px bg-gradient-to-r from-gold-300 via-gold-400 to-gold-300" />

          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="relative p-8 lg:p-10 text-center lg:text-left">
                {/* Number Badge */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-ink-900 text-gold-400 font-display text-2xl font-bold rounded-full mb-6 relative z-10 shadow-luxury">
                  {step.number}
                </div>

                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center bg-gold-100 text-gold-600 rounded-lg mb-6 mx-auto lg:mx-0">
                  {step.icon}
                </div>

                {/* Content */}
                <h3 className="font-display text-2xl font-bold text-ink-900 mb-4">
                  {step.title}
                </h3>

                <p className="font-body text-ink-600 leading-relaxed mb-4">
                  {step.description}
                </p>

                {/* Accent */}
                <div className="flex items-center gap-2 justify-center lg:justify-start text-gold-600 text-sm font-sans">
                  <span className="w-4 h-px bg-gold-400" />
                  <span className="italic">{step.accent}</span>
                </div>
              </div>

              {/* Arrow (Mobile) */}
              {index < steps.length - 1 && (
                <div className="lg:hidden flex justify-center my-4">
                  <ArrowRight className="w-6 h-6 text-gold-400 rotate-90" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <button
            onClick={scrollToContact}
            className="btn-primary"
          >
            <span className="flex items-center gap-3">
              Begin Your Journey
              <ArrowRight className="w-5 h-5" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default WorkingProcess;
